import { useTranslate } from '@growtherapy/i18n';
import { Alert, Text } from '@growtherapy/sprout-ui';

import Blur from '../../assets/backgrounds/thumb/blur.jpg';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import {
  type BackgroundSettings,
  BackgroundType,
  colorBackgrounds,
  patternBackgrounds,
  sceneBackgrounds,
} from '../../video-sdk';
import { SelfVideoThumbnail } from '../../visitor/SelfVideoThumbnail';
import { BackgroundButton } from './BackgroundButton';
import { BackgroundThumbnailGrid } from './BackgroundThumbnailGrid';

export function BackgroundSettingsTab({
  setBackgroundSettings,
  isMirrored,
  hasBackgroundProcessorFailure,
  backgroundsSelectionIsDisabled,
  selectedBackground = BackgroundType.NONE,
  selectedBackgroundIndex = 0,
  helpText,
  isDarkTheme,
}: {
  setBackgroundSettings: (backgroundSettings: BackgroundSettings) => void;
  isMirrored: boolean;
  hasBackgroundProcessorFailure: boolean;
  backgroundsSelectionIsDisabled?: boolean;
  selectedBackground?: BackgroundType;
  selectedBackgroundIndex?: number;
  helpText?: string;
  isDarkTheme: boolean;
}) {
  const t = useTranslate();
  return (
    <div className='flex flex-col gap-4'>
      <div className='flex justify-center'>
        <SelfVideoThumbnail
          videoClassName='max-h-none max-w-none w-full h-[216px]'
          className='h-full w-full'
          shouldShowHideSelf={false}
          showExpandVideoToggle={false}
          showSetPositionMenu={false}
          isSettingsThumbnail={true}
          {...(helpText && {
            helpText,
          })}
        />
      </div>
      {hasBackgroundProcessorFailure && (
        <Alert
          use='warning'
          className='rounded-lg p-4 sm:p-4 m-0'
          heading={t('telehealth-core:settings.notSeeingABackgroundChange')}
        >
          <Text variant='sm'>{t('telehealth-core:settings.yourDeviceOrBrowserMayNotSupportVirtualBackgrounds')}</Text>
        </Alert>
      )}
      <div className='grid grid-cols-2 gap-4 pb-3'>
        <BackgroundButton
          isDisabled={backgroundsSelectionIsDisabled}
          isSelected={selectedBackground === BackgroundType.NONE}
          text={t('telehealth-core:settings.none')}
          isLarge
          isDarkTheme={isDarkTheme}
          onClick={() =>
            setBackgroundSettings({
              backgroundType: BackgroundType.NONE,
            })
          }
        />
        <BackgroundButton
          isDisabled={backgroundsSelectionIsDisabled}
          image={Blur}
          text={t('telehealth-core:settings.blur')}
          isLarge
          isDarkTheme={isDarkTheme}
          isSelected={selectedBackground === BackgroundType.BLUR}
          onClick={() => {
            sendLoggingEvents(TrackingEvents.BLUR_BACKGROUND_USE);
            setBackgroundSettings({
              backgroundType: BackgroundType.BLUR,
            });
          }}
        />
      </div>
      <BackgroundThumbnailGrid
        title={t('telehealth-core:settings.scenes')}
        images={sceneBackgrounds}
        isMirrored={isMirrored}
        setBackgroundSettings={setBackgroundSettings}
        selectedBackground={selectedBackground}
        selectedBackgroundIndex={selectedBackgroundIndex}
        backgroundsSelectionIsDisabled={backgroundsSelectionIsDisabled}
        isDarkTheme={isDarkTheme}
      />
      <BackgroundThumbnailGrid
        title={t('telehealth-core:settings.patterns')}
        images={patternBackgrounds}
        isMirrored={isMirrored}
        setBackgroundSettings={setBackgroundSettings}
        selectedBackground={selectedBackground}
        selectedBackgroundIndex={selectedBackgroundIndex}
        backgroundsSelectionIsDisabled={backgroundsSelectionIsDisabled}
        isDarkTheme={isDarkTheme}
        hasBorder
      />
      <BackgroundThumbnailGrid
        title={t('telehealth-core:settings.colors')}
        images={colorBackgrounds}
        isMirrored={isMirrored}
        setBackgroundSettings={setBackgroundSettings}
        selectedBackground={selectedBackground}
        selectedBackgroundIndex={selectedBackgroundIndex}
        backgroundsSelectionIsDisabled={backgroundsSelectionIsDisabled}
        isDarkTheme={isDarkTheme}
        hasBorder
      />
    </div>
  );
}
